import { useEffect, useState, useCallback } from 'react';

export type FetchConversionCurrencies = {
  AUD: number;
  BGN: number;
  BRL: number;
  CAD: number;
  CHF: number;
  CNY: number;
  CZK: number;
  DKK: number;
  EUR: number;
  GBP: number;
  HKD: number;
  HRK: number;
  HUF: number;
  IDR: number;
  ILS: number;
  INR: number;
  ISK: number;
  JPY: number;
  KRW: number;
  MXN: number;
  MYR: number;
  NOK: number;
  NZD: number;
  PHP: number;
  PLN: number;
  RON: number;
  RUB: number;
  SEK: number;
  SGD: number;
  THB: number;
  TRY: number;
  USD: number;
  ZAR: number;
};

interface Conversions {
  success?: boolean;
  // The base currency, by default GBP
  base: keyof FetchConversionCurrencies;
  // yyyy-mm-dd format
  date: string;
  rates: FetchConversionCurrencies;
}

const fallbackRates = {
  success: false,
  base: 'GBP' as keyof FetchConversionCurrencies,
  date: '1970-01-01',
  rates: {
    AED: 0,
    AFN: 0,
    ALL: 0,
    AMD: 0,
    ANG: 0,
    AOA: 0,
    ARS: 0,
    AUD: 0,
    AWG: 0,
    AZN: 0,
    BAM: 0,
    BBD: 0,
    BDT: 0,
    BGN: 0,
    BHD: 0,
    BIF: 0,
    BMD: 0,
    BND: 0,
    BOB: 0,
    BRL: 0,
    BSD: 0,
    BTC: 0,
    BTN: 0,
    BWP: 0,
    BYN: 0,
    BYR: 0,
    BZD: 0,
    CAD: 0,
    CDF: 0,
    CHF: 0,
    CLF: 0,
    CLP: 0,
    CNY: 0,
    COP: 0,
    CRC: 0,
    CUC: 0,
    CUP: 0,
    CVE: 0,
    CZK: 0,
    DJF: 0,
    DKK: 0,
    DOP: 0,
    DZD: 0,
    EGP: 0,
    ERN: 0,
    ETB: 0,
    EUR: 0,
    FJD: 0,
    FKP: 0,
    GBP: 0,
    GEL: 0,
    GGP: 0,
    GHS: 0,
    GIP: 0,
    GMD: 0,
    GNF: 0,
    GTQ: 0,
    GYD: 0,
    HKD: 0,
    HNL: 0,
    HRK: 0,
    HTG: 0,
    HUF: 0,
    IDR: 0,
    ILS: 0,
    IMP: 0,
    INR: 0,
    IQD: 0,
    IRR: 0,
    ISK: 0,
    JEP: 0,
    JMD: 0,
    JOD: 0,
    JPY: 0,
    KES: 0,
    KGS: 0,
    KHR: 0,
    KMF: 0,
    KPW: 0,
    KRW: 0,
    KWD: 0,
    KYD: 0,
    KZT: 0,
    LAK: 0,
    LBP: 0,
    LKR: 0,
    LRD: 0,
    LSL: 0,
    LTL: 0,
    LVL: 0,
    LYD: 0,
    MAD: 0,
    MDL: 0,
    MGA: 0,
    MKD: 0,
    MMK: 0,
    MNT: 0,
    MOP: 0,
    MRO: 0,
    MUR: 0,
    MVR: 0,
    MWK: 0,
    MXN: 0,
    MYR: 0,
    MZN: 0,
    NAD: 0,
    NGN: 0,
    NIO: 0,
    NOK: 0,
    NPR: 0,
    NZD: 0,
    OMR: 0,
    PAB: 0,
    PEN: 0,
    PGK: 0,
    PHP: 0,
    PKR: 0,
    PLN: 0,
    PYG: 0,
    QAR: 0,
    RON: 0,
    RSD: 0,
    RUB: 0,
    RWF: 0,
    SAR: 0,
    SBD: 0,
    SCR: 0,
    SDG: 0,
    SEK: 0,
    SGD: 0,
    SHP: 0,
    SLL: 0,
    SOS: 0,
    SRD: 0,
    STD: 0,
    SVC: 0,
    SYP: 0,
    SZL: 0,
    THB: 0,
    TJS: 0,
    TMT: 0,
    TND: 0,
    TOP: 0,
    TRY: 0,
    TTD: 0,
    TWD: 0,
    TZS: 0,
    UAH: 0,
    UGX: 0,
    USD: 0,
    UYU: 0,
    UZS: 0,
    VEF: 0,
    VND: 0,
    VUV: 0,
    WST: 0,
    XAF: 0,
    XAG: 0,
    XAU: 0,
    XCD: 0,
    XDR: 0,
    XOF: 0,
    XPF: 0,
    YER: 0,
    ZAR: 0,
    ZMK: 0,
    ZMW: 0,
    ZWL: 0,
  },
};

export const fetchConversionRates = async (
  api_key: string,
): Promise<Conversions> => {
  try {
    const result: Conversions = await (
      await fetch(
        `https://api.exchangeratesapi.io/latest?base=GBP&access_key=${api_key}`,
      )
    )?.json();

    if (result?.success == true) return result;
  } catch (e) {
    console.warn(e);
  }

  return fallbackRates;
};

export type ConvertFunction = (params: {
  base: keyof FetchConversionCurrencies;
  target: keyof FetchConversionCurrencies;
  amount: number;
}) => number | null;

/**
 * Returns a function you can use to calculate the
 * exchange rates between currencies
 */
export const useFetchConversionRates = (api_key: string) => {
  const [data, setData] = useState<Conversions | null>(null);
  const [error, setError] = useState<string>();

  const fetchRates = useCallback(() => {
    try {
      fetchConversionRates(api_key).then(setData);
    } catch (e) {
      setError('An unknown error occurred fetching the conversion rates.');
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  let state: 'pending' | 'error' | 'success' = 'pending';
  if (error) {
    state = 'error';
  } else if (data) {
    state = 'success';
  }

  const convert: ConvertFunction = ({ base, amount, target }) => {
    if (state !== 'success' || !data) {
      console.error(
        'You cannot convert if useFetchConversionRates has errored or is still loading.',
      );
      return null;
    }
    const baseModifier = data.rates[base];
    const targetModifier = data.rates[target];
    return Number(((amount * targetModifier) / baseModifier).toFixed(2));
  };

  return {
    data,
    error,
    state,
    refetch: fetchRates,
    convert,
  };
};
